/**
 * Fossil Eras (docs/fossil-eras-plan.md): the constants a rebirth turns on.
 * The logic that uses them is pure and lives in game/rebirth.ts.
 */

import type { BackgroundName } from '../ui/sprites';

/**
 * Eggs the **first** era must lay before the Extinction can be called. About
 * six hours of active play, or day 3–4 for a casual player — set low on
 * purpose, where the herd is still small enough that losing it does not
 * sting. Later eras ask for more; see `extinctionThreshold`.
 */
export const EXTINCTION_THRESHOLD_BASE = 1e11;

/**
 * What each era asks for over the last one.
 *
 * A fixed threshold was the mistake this replaces. Everything that crosses an
 * era — the fossil bonus, the Museum perks — grows without bound, so a target
 * that never moved was eventually cleared in minutes by whatever the last era
 * left behind, and the loop stopped being a game and became a button. Moving
 * the target is what gives carried-over power something to push against.
 */
export const ERA_GROWTH = 2;

/** Eggs era `era` (0-based, so the first era is 0) must lay to end. */
export function extinctionThreshold(era: number): number {
  return EXTINCTION_THRESHOLD_BASE * ERA_GROWTH ** Math.max(0, era);
}

/**
 * Fossils are `floor(cbrt(eggsThisEra / fossilScale(era)))`: 3 at the
 * threshold, 6 at ten times it, 14 at a hundred.
 *
 * It was a square root, and that was too generous to waiting. Production
 * grows faster within an era than a square root takes away, so a player who
 * stayed until an era doubled their fossils ended a simulated week with 140×
 * the fossils of one who left at the line — and would keep pulling away. The
 * cube root leaves the early extinctions where they were and cuts that spread
 * to about 4×, so staying longer still pays but leaving is a real choice.
 */
export const FOSSIL_SCALE_BASE = 3e9;

/**
 * The scale rides the same growth as the threshold, so an era is always worth
 * three fossils at the line whatever its number. Without that, a threshold
 * that doubles would hand out `cbrt(2)` times the fossils every era, and
 * fossils would compound exactly the way this change exists to stop.
 */
export function fossilScale(era: number): number {
  return FOSSIL_SCALE_BASE * ERA_GROWTH ** Math.max(0, era);
}

/** Production multiplier added by each fossil *held*; spending one stops it. */
export const FOSSIL_BONUS = 0.05;

/**
 * Era modifiers. Each new era is played under one the player picks from
 * three; the first era is plain. A modifier is a set of multipliers and
 * clock changes read from data, not a branch in the systems it touches, so
 * adding one is a row here.
 *
 * Every one must be a *trade*. The pick offers three so a bad draw for how
 * you like to play is avoidable, but none of them should be strictly worse
 * than plain — that would just be a wasted era.
 */
export interface EraModifier {
  id: string;
  name: string;
  description: string;
  /**
   * The painting this era is played against (ui/sprites.ts BACKGROUNDS).
   * Left out, it plays against the default one.
   */
  background?: BackgroundName;
  /** The dinosaurs this favours, by id, and by how much. */
  favours?: { dinos: readonly string[]; mult: number };
  /** Applied to every dinosaur outside `favours`. */
  others?: number;
  /** Flyers only, on top of the above. */
  flyers?: number;
  /** Dinosaurs that start the era already hatched (level 1), by id. */
  freeHatch?: readonly string[];
  /** Multiplier on what a click is worth. */
  clicks?: number;
  /** Multiplier on the offline rate. */
  offline?: number;
  /** Cost multiplier for these dinosaurs, by id. */
  cost?: { dinos: readonly string[]; mult: number };
  /** Seconds between meteors, or null for a sky with none. */
  meteorGap?: number | null;
  /** Seconds between golden eggs, or null for none. */
  goldenGap?: number | null;
  /** Multiplier on shards paid. */
  shards?: number;
}

export const ERA_MODIFIERS: readonly EraModifier[] = [
  {
    background: 'jurassic',
    id: 'era:jurassic',
    name: 'Jurassic',
    description: 'The middle of the roster thrives; everything else is a little slower.',
    // The middle of the roster, with the era's own Allosaurus among them.
    favours: {
      dinos: ['triceratops', 'stegosaurus', 'allosaurus', 'ankylosaurus', 'parasaurolophus'],
      mult: 2,
    },
    others: 0.8,
  },
  {
    background: 'cretaceous',
    id: 'era:cretaceous',
    name: 'Cretaceous',
    description: 'Flyers lay three times as much, and the Pteranodon arrives already hatched.',
    flyers: 3,
    freeHatch: ['pteranodon'],
  },
  {
    background: 'ice-age',
    id: 'era:ice-age',
    name: 'Ice Age',
    description: 'Away rate doubled; clicking is worth half.',
    offline: 2,
    clicks: 0.5,
  },
  {
    background: 'volcanic',
    id: 'era:volcanic',
    name: 'Volcanic',
    description: 'Meteors every 90 seconds and double shards, but no golden eggs.',
    meteorGap: 90,
    shards: 2,
    goldenGap: null,
  },
  {
    background: 'night',
    id: 'era:night',
    name: 'Night',
    description: 'Golden eggs every 90 seconds, but nothing falls from space.',
    goldenGap: 90,
    meteorGap: null,
  },
  {
    background: 'giant',
    id: 'era:giant',
    name: 'Giant',
    description: 'The last three tiers cost a tenth, and lay half.',
    cost: { dinos: ['mosasaurus', 'quetzalcoatlus', 'baryonyx'], mult: 0.1 },
    favours: { dinos: ['mosasaurus', 'quetzalcoatlus', 'baryonyx'], mult: 0.5 },
  },
];

export const ERA_MODIFIERS_BY_ID: ReadonlyMap<string, EraModifier> = new Map(
  ERA_MODIFIERS.map((e) => [e.id, e]),
);

/** How many modifiers the player chooses between after an extinction. */
export const ERA_CHOICES = 3;

/**
 * Expeditions: an era run under a rule, for a bigger payout. One at a time,
 * chosen when the era is, and the rule shows under the egg all era.
 *
 * These are for a player who has done the loop enough times that the normal
 * era holds no surprises — a reason to play differently rather than faster.
 */
export interface Expedition {
  id: string;
  name: string;
  description: string;
  /** Multiplier on the fossils the era pays. */
  fossils: number;
  /** No upgrades may be bought. */
  noUpgrades?: boolean;
  /** Clicking the egg is worth nothing. */
  noClicks?: boolean;
  /** The Extinction fires itself after this many seconds of wall time. */
  endsAfterSeconds?: number;
  /** The payout multiplier only applies if the era laid at least this much. */
  requiresEggs?: number;
  /** Dinosaurs that start the era already hatched (level 1), by id. */
  freeHatch?: readonly string[];
}

export const EXPEDITIONS: readonly Expedition[] = [
  {
    id: 'trip:barren',
    name: 'Barren',
    description: 'No upgrades all era. Fossils are doubled.',
    fossils: 2,
    noUpgrades: true,
  },
  {
    id: 'trip:hands-off',
    name: 'Hands Off',
    description:
      'Clicking the egg does nothing, so a Compsognathus starts hatched. 50% more fossils.',
    fossils: 1.5,
    noClicks: true,
    // Without it the era's first eggs had to come from a perk or a golden
    // egg's Jackpot, and a Volcanic era has no golden eggs: with neither
    // Hatchery nor fossils held for Old Bones, nothing could ever be laid.
    freeHatch: ['compsognathus'],
  },
  {
    id: 'trip:sprint',
    name: 'Sprint',
    description: 'The era ends itself after 24 hours. Triple fossils if it laid a trillion.',
    fossils: 3,
    endsAfterSeconds: 24 * 60 * 60,
    requiresEggs: 1e12,
  },
];

export const EXPEDITIONS_BY_ID: ReadonlyMap<string, Expedition> = new Map(
  EXPEDITIONS.map((e) => [e.id, e]),
);
